class DrawerDialog extends HTMLElement {
  constructor() {
    super();

    this.overlayClickHandler = this.close.bind(this);
    this.keyupHandler = (evt) => evt.code === 'Escape' && this.close();
  }

  connectedCallback() {
    if (this.initialized) {
      this.bindOverlayClick();
      this.onDrawerConnected();
      return;
    }

    this.initialized = true;
    this.addEventListener('keyup', this.keyupHandler);
    this.bindOverlayClick();
    this.onDrawerConnected();
  }

  onDrawerConnected() {}

  bindOverlayClick() {
    const overlay = this.getOverlay();
    if (!overlay || overlay.dataset.drawerBound === 'true') return;

    overlay.addEventListener('click', this.overlayClickHandler);
    overlay.dataset.drawerBound = 'true';
  }

  getOverlay() {
    return this.querySelector('.cart-drawer__overlay, .drawer__overlay');
  }

  getDialog() {
    return this.querySelector('.drawer__inner');
  }

  getTrapFocusContainer() {
    if (this.classList.contains('is-empty')) return this.querySelector('.drawer__inner-empty');

    const dialog = this.getDialog();
    return dialog?.parentElement || dialog;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const drawerNote = this.querySelector('[id^="Details-"] summary');
    if (drawerNote && !drawerNote.hasAttribute('role')) this.setSummaryAccessibility(drawerNote);

    // The animation does not always trigger without a tiny delay.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.getTrapFocusContainer();
      const focusElement = this.getDialog() || this.querySelector('.drawer__close');

      if (containerToTrapFocusOn && focusElement) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    if (this.activeElement) this.activeElement.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('active');
    if (this.activeElement) this.activeElement.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(drawerNote) {
    drawerNote.setAttribute('role', 'button');
    drawerNote.setAttribute('aria-expanded', 'false');

    if (drawerNote.nextElementSibling.getAttribute('id')) {
      drawerNote.setAttribute('aria-controls', drawerNote.nextElementSibling.id);
    }

    drawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    drawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

class CartDrawer extends DrawerDialog {
  onDrawerConnected() {
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerTriggerBound === 'true') return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
    cartLink.dataset.drawerTriggerBound = 'true';
  }

  renderContents(parsedState, isUpsell = false) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.bindOverlayClick();
      if (!isUpsell) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class FinancialPaymentMethodsDrawer extends DrawerDialog {
  onDrawerConnected() {
    this.setTriggerAccessibility();
  }

  setTriggerAccessibility() {
    if (!this.id) return;

    document.querySelectorAll(`[data-financial-payment-methods-trigger="${this.id}"]`).forEach((trigger) => {
      if (trigger.dataset.drawerTriggerBound === 'true') return;

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(trigger);
        }
      });
      trigger.dataset.drawerTriggerBound = 'true';
    });
  }
}

if (!customElements.get('financial-payment-methods-drawer')) {
  customElements.define('financial-payment-methods-drawer', FinancialPaymentMethodsDrawer);
}
